import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, ChevronDown, Heart } from "lucide-react";
import { useApp } from "../context/AppContext";
import { CURRENCIES } from "../context/AppContext";
import { tr, LANGS } from "../i18n/translations";

export default function Navbar() {
  const { lang, setLang, currency, setCurrency, fxInfo, currencyAutoDetected, cartCount } = useApp();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const links = [
    { to: "/ressources-a-telecharger", label: "Ressources PDF" },
    { to: "/logiciels", label: "Applications" },
    { to: "/catalogue", label: "Matériel" },
    { to: "/outils", label: "Outils gratuits", badge: "Nouveau" },
  ];

  function submitSearch(e) {
    e.preventDefault();
    if (q.trim()) navigate(`/recherche?q=${encodeURIComponent(q.trim())}`);
  }

  return (
    <header className="sticky top-0 z-50 bg-white shadow-[0_4px_16px_rgba(15,23,42,0.08)] border-b border-ink/5">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-12 py-4 flex items-center gap-3 lg:gap-6">
        <Link to="/" data-testid="nav-logo" className="font-display text-xl md:text-2xl tracking-tight shrink-0">
          orthophonie<span className="italic font-light">.online</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 text-[13px] font-semibold uppercase tracking-wider shrink-0">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to}`}
              className={({ isActive }) =>
                `relative py-1 transition-colors after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-[2px] after:bg-pedablue after:transition-all after:origin-center ${
                  isActive
                    ? "text-ink after:scale-x-100"
                    : "text-ink/85 hover:text-ink after:scale-x-0 hover:after:scale-x-100"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
              {l.badge && (
                <span className="ms-1.5 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full align-top" style={{ background: "linear-gradient(145deg,#F5C443,#D9A62B)", color: "#4A2F9E" }}>
                  {l.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden lg:flex items-center gap-2.5 bg-ivory-100 hover:bg-ivory-200 focus-within:bg-white focus-within:ring-2 focus-within:ring-pedablue/40 border border-ink/10 rounded-full px-5 py-2.5 flex-1 max-w-xl transition-all shadow-inner">
          <Search size={18} className="text-ink/60 shrink-0" />
          <input
            data-testid="nav-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher : dyslexie, pack phonologie, articulation…"
            className="bg-transparent outline-none text-sm flex-1 min-w-0 placeholder:text-ink/40"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              aria-label="Effacer"
              className="text-ink/40 hover:text-ink shrink-0"
            >
              <X size={14} />
            </button>
          )}
        </form>

        <div className="flex-1 lg:hidden" />

        <div className="relative">
          <button
            data-testid="nav-currency-btn"
            onClick={() => setCurrencyOpen((o) => !o)}
            className="hidden sm:flex items-center gap-1 text-sm px-3 py-2 rounded-full hover:bg-ivory-200"
            title="Devise d'affichage (paiement final en EUR)"
          >
            <span className="font-medium">{currency}</span>
            <ChevronDown size={14} />
          </button>
          {currencyOpen && (
            <div className="absolute end-0 mt-2 bg-white rounded-2xl shadow-xl border border-ink/10 p-2 min-w-[240px] z-50">
              {currencyAutoDetected && (
                <p className="text-[10px] uppercase tracking-widest text-pedablue bg-pedablue/10 rounded-xl px-3 py-1.5 mb-1.5 text-center">
                  📍 Devise détectée automatiquement
                </p>
              )}
              {CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  data-testid={`currency-opt-${c.code}`}
                  onClick={() => { setCurrency(c.code); setCurrencyOpen(false); }}
                  className={`w-full text-sm text-start flex items-center justify-between gap-2 px-3 py-2 rounded-xl hover:bg-ivory-200 ${currency === c.code ? "bg-ivory-200" : ""}`}
                >
                  <span className="flex items-center gap-2">
                    <span>{c.flag}</span>
                    <span>{c.code} <span className="text-ink-soft text-xs">— {c.name}</span></span>
                  </span>
                  {currency === c.code && <span className="text-pedablue text-xs">✓</span>}
                </button>
              ))}
              <p className="text-[10px] text-ink-soft px-3 pt-2 pb-1 border-t border-ink/5 mt-1">
                Affichage uniquement · paiement final en EUR.
                {fxInfo?.source === "frankfurter" && fxInfo?.date && (
                  <> Taux ECB du <strong>{fxInfo.date}</strong>.</>
                )}
                {fxInfo?.source === "fallback" && (
                  <> Taux indicatifs (hors-ligne).</>
                )}
              </p>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            data-testid="nav-lang-btn"
            onClick={() => setLangOpen((o) => !o)}
            className="hidden sm:flex items-center gap-1 text-sm px-3 py-2 rounded-full hover:bg-ivory-200"
          >
            <span>{(LANGS.find((l) => l.code === lang) || LANGS[0]).flag}</span>
            <ChevronDown size={14} />
          </button>
          {langOpen && (
            <div className="absolute end-0 mt-2 bg-white rounded-2xl shadow-xl border border-ink/10 p-2 grid grid-cols-2 gap-1 min-w-[260px]">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-opt-${l.code}`}
                  onClick={() => { setLang(l.code); setLangOpen(false); }}
                  className={`text-sm text-start flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-ivory-200 ${lang === l.code ? "bg-ivory-200" : ""}`}
                >
                  <span>{l.flag}</span>
                  <span>{l.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Link to="/favoris" data-testid="nav-favoris" aria-label="Mes favoris" className="relative p-2 rounded-full hover:bg-ivory-200 flex flex-col items-center shrink-0">
          <Heart size={20} />
          <span className="text-[9px] uppercase tracking-widest mt-0.5 hidden sm:inline">Favoris</span>
        </Link>

        <Link to="/cart" data-testid="nav-cart" aria-label="Mon panier" className="relative p-2 rounded-full hover:bg-ivory-200 flex flex-col items-center shrink-0">
          <ShoppingBag size={20} />
          <span className="text-[9px] uppercase tracking-widest mt-0.5 hidden sm:inline">Panier</span>
          {cartCount > 0 && (
            <span className="absolute -top-1 -end-1 bg-pedablue text-ivory text-[10px] rounded-full w-5 h-5 flex items-center justify-center" data-testid="nav-cart-count">
              {cartCount}
            </span>
          )}
        </Link>

        <button className="lg:hidden p-1 shrink-0" onClick={() => setOpen((o) => !o)} data-testid="nav-menu-toggle" aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-ink/10 px-6 py-4 space-y-3 bg-ivory" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm">{l.label}</Link>
          ))}
          <div className="pt-3 mt-3 border-t border-ink/10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-ink/50">Devise</span>
              <select
                data-testid="mobile-currency-select"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="text-sm px-2 py-1 rounded-lg bg-ivory-200 border-0"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-ink/50">Langue</span>
              <select
                data-testid="mobile-lang-select"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="text-sm px-2 py-1 rounded-lg bg-ivory-200 border-0"
              >
                {LANGS.map((l) => (
                  <option key={l.code} value={l.code}>{l.flag} {l.code.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="pt-3 mt-3 border-t border-ink/10">
            <Link
              to="/mes-ressources"
              onClick={() => setOpen(false)}
              className="block text-sm py-1.5"
              data-testid="mobile-nav-mes-ressources"
            >
              Mes ressources téléchargées
            </Link>
            <Link
              to="/mes-commandes"
              onClick={() => setOpen(false)}
              className="block text-sm py-1.5"
              data-testid="mobile-nav-mes-commandes"
            >
              Mes commandes
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
